<app-navbar />

<main id="main-content">
  <section class="page-hero">
    <div class="container">
      <app-breadcrumb [trail]="[{ label: 'Security and compliance' }]" />
      <span class="eyebrow">Trust centre</span>
      <h1>Security and compliance, documented for your vendor review</h1>
      <p>
        You are handing us identity data about your customers. This page sets out exactly how it is
        encrypted, who can reach it, how long we keep it and how our information security program
        maps to the FTC Safeguards Rule. Where a control is still in progress, we say so.
      </p>
      <div class="actions-row center mt-32">
        <a class="btn btn-primary btn-lg" [routerLink]="[routes.contact.url]">Request the security package</a>
        <a class="btn btn-outline btn-lg" [routerLink]="[]" fragment="safeguards">See the GLBA control mapping</a>
      </div>
      <p class="trust-meta">Last reviewed {{ lastReviewed }} &middot; Questions to <a [href]="'mailto:' + site.supportEmail">{{ site.supportEmail }}</a></p>
    </div>
  </section>

  <!-- Framework posture. Honest status labels, no implied certifications. -->
  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Where we stand</span>
        <h2 class="section-title">Frameworks and attestations</h2>
        <p class="section-lead">
          Our program is built against the rule our financial institution customers are actually
          examined against, and mapped to the frameworks your reviewers already work in.
        </p>
      </div>

      <div class="grid-4 attestation-cards">
        @for (item of attestations; track item.name) {
          <article class="feature-card attestation-card" [class.is-progress]="item.state === 'progress'">
            <span class="icon-badge" [innerHTML]="iconFor(item.icon)"></span>
            <span class="attestation-chip">{{ item.status }}</span>
            <h3>{{ item.name }}</h3>
            <p>{{ item.description }}</p>
          </article>
        }
      </div>

      <p class="disclaimer">
        We do not hold a completed SOC 2 report today and we will not imply otherwise. If a
        completed report is a hard requirement for your procurement process, talk to us early and we
        will share our audit timeline and the interim control evidence we can provide in the
        meantime.
      </p>

      <div class="roadmap-panel">
        <div class="roadmap-head">
          <span class="eyebrow">SOC 2 readiness</span>
          <h3>Exactly where we are on the path to a report</h3>
          <p>
            Most vendors say the words "in progress" and leave it there. Here is the actual sequence,
            what is finished, and what we are working on right now.
          </p>
        </div>
        <ol class="roadmap">
          @for (milestone of roadmap; track milestone.title) {
            <li class="roadmap-step" [class.is-done]="milestone.state === 'done'"
              [class.is-current]="milestone.state === 'current'">
              <span class="roadmap-marker" aria-hidden="true"></span>
              <div class="roadmap-content">
                <span class="roadmap-phase">{{ milestone.phase }}</span>
                <h4>{{ milestone.title }}</h4>
                <p>{{ milestone.description }}</p>
              </div>
              <span class="roadmap-state">
                {{ milestone.state === "done" ? "Complete" : milestone.state === "current" ? "In progress" : "Planned" }}
              </span>
            </li>
          }
        </ol>
      </div>
    </div>
  </section>

  <!-- Control areas, expanded with the bullet detail reviewers ask for. -->
  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Controls</span>
        <h2 class="section-title">How your data is protected</h2>
        <p class="section-lead">
          Six control areas, each with the specifics rather than a reassuring adjective.
        </p>
      </div>

      <div class="grid-2 control-grid">
        @for (control of controlAreas; track control.title) {
          <article class="card control-card">
            <div class="control-head">
              <span class="icon-badge" [innerHTML]="iconFor(control.icon)"></span>
              <div>
                <h3>{{ control.title }}</h3>
                <p>{{ control.description }}</p>
              </div>
            </div>
            <ul class="check-list control-details">
              @for (detail of control.details; track detail) {
                <li>{{ detail }}</li>
              }
            </ul>
          </article>
        }
      </div>
    </div>
  </section>

  <!-- The FTC Safeguards Rule mapping. This is the table reviewers screenshot. -->
  <section class="section" id="safeguards">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">GLBA Safeguards Rule</span>
        <h2 class="section-title">Our control mapping to 16 CFR Part 314</h2>
        <p class="section-lead">
          The FTC Safeguards Rule requires covered financial institutions to maintain a documented
          information security program. Here is each requirement and what we do to meet it, so you
          can drop it straight into your own assessment.
        </p>
      </div>

      <div class="mapping-table-wrap">
        <table class="mapping-table">
          <thead>
            <tr>
              <th scope="col">Citation</th>
              <th scope="col">What the rule requires</th>
              <th scope="col">What VerifyDeceased does</th>
            </tr>
          </thead>
          <tbody>
            @for (row of safeguards; track row.citation) {
              <tr>
                <th scope="row"><code>{{ row.citation }}</code></th>
                <td>{{ row.requirement }}</td>
                <td class="muted">{{ row.implementation }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <p class="disclaimer">
        This mapping describes our own information security program. It is provided to support your
        due diligence and is not legal advice. You remain responsible for your own Safeguards Rule
        obligations, including the oversight of us as a service provider under §&nbsp;314.4(f).
      </p>
    </div>
  </section>

  <!-- Retention schedule and hosting. -->
  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Data lifecycle</span>
        <h2 class="section-title">What we keep, and for how long</h2>
        <p class="section-lead">
          The shortest honest answer to "what happens to our file after you match it". We do not
          sell, resell or license the records you send us, and we do not use them to enrich data we
          sell to anyone else.
        </p>
      </div>

      <div class="card retention-card">
        <table class="mapping-table">
          <thead>
            <tr>
              <th scope="col">Data</th>
              <th scope="col">Retention</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            @for (row of retention; track row.dataType) {
              <tr>
                <th scope="row">{{ row.dataType }}</th>
                <td>{{ row.retention }}</td>
                <td class="muted">{{ row.note }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="grid-2 align-start mt-32">
        <div class="card">
          <h3>Deletion on request</h3>
          <p>
            You can request deletion of a specific batch at any time and we will confirm once it is
            done. Account level deletion is available on closure, subject to the billing and audit
            records we are legally required to retain.
          </p>
        </div>
        <div class="card">
          <h3>Hosting and data residency</h3>
          <p>
            All customer data is processed and stored in United States regions, including backups.
            The named subprocessor list covering hosting, payments, transactional email and
            observability is provided in the security package.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Subprocessors. -->
  <section class="section">
    <div class="container">
      <div class="grid-2 align-start">
        <div>
          <span class="eyebrow">Service providers</span>
          <h2 class="section-title">Subprocessors and vendor oversight</h2>
          <p class="section-lead">
            Under §&nbsp;314.4(f) you are accountable for the providers we use. Every provider that
            can touch customer information is assessed before engagement, bound by written security
            obligations including breach notification, and reassessed on a risk based cadence.
          </p>
          <div class="actions-row">
            <a class="btn btn-outline" [routerLink]="[routes.contact.url]">Get the named list</a>
          </div>
        </div>
        <div class="card table-card">
          <table class="mapping-table compact">
            <thead>
              <tr>
                <th scope="col">Purpose</th>
                <th scope="col">Data handled</th>
                <th scope="col">Region</th>
              </tr>
            </thead>
            <tbody>
              @for (row of subprocessors; track row.purpose) {
                <tr>
                  <th scope="row">{{ row.purpose }}</th>
                  <td class="muted">{{ row.dataHandled }}</td>
                  <td>{{ row.location }}</td>
                </tr>
              }
            </tbody>
          </table>
          <p class="table-note">
            Providers are named in the security package rather than published here, so the list you
            receive is always the current one.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Incident response and continuity. -->
  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">When something goes wrong</span>
        <h2 class="section-title">Incident response and continuity</h2>
        <p class="section-lead">
          A plan that only exists on paper is not a plan. Ours defines who acts, how fast, and what
          you hear from us.
        </p>
      </div>

      <div class="grid-3">
        <article class="feature-card">
          <span class="icon-badge" [innerHTML]="iconFor('alert')"></span>
          <h3>Incident response</h3>
          <p>
            A documented plan covering detection, containment, investigation, recovery and post
            incident review, with defined severity levels, named owners and escalation paths. The
            plan is exercised at least annually.
          </p>
        </article>
        <article class="feature-card">
          <span class="icon-badge" [innerHTML]="iconFor('phone')"></span>
          <h3>Notifying you</h3>
          <p>
            If an incident affects your data we notify you without undue delay with what we know,
            what we are doing about it and what we need from you, then follow up with the outcome of
            the investigation and the corrective actions taken.
          </p>
        </article>
        <article class="feature-card">
          <span class="icon-badge" [innerHTML]="iconFor('refresh')"></span>
          <h3>Backups and recovery</h3>
          <p>
            Backups are encrypted and restoration is tested, with defined recovery time and recovery
            point objectives for the systems that hold customer information. Continuity plans are
            reviewed after material change.
          </p>
        </article>
      </div>
    </div>
  </section>

  <!-- Commitments and permissible use. -->
  <section class="section">
    <div class="container">
      <div class="grid-2 align-start">
        <div>
          <span class="eyebrow">Our commitments</span>
          <h2 class="section-title">What you can count on</h2>
          <p class="section-lead">
            These are the commitments we make to every customer who trusts us with their data. If we
            cannot keep one of them, we will tell you rather than quietly change the wording.
          </p>
          <div class="actions-row">
            <a class="btn btn-outline" [routerLink]="[routes.privacy.url]">Read the privacy policy</a>
          </div>
        </div>
        <ul class="check-list">
          @for (commitment of commitments; track commitment) {
            <li>{{ commitment }}</li>
          }
        </ul>
      </div>

      <div class="grid-2 align-start mt-32">
        <div class="card">
          <h3>Data handling</h3>
          <p>
            You submit only the identity fields needed to run a match. We process those records to
            return a result, then delete them on a defined schedule. We do not sell the records you
            send us and they never become part of a product we sell to someone else.
          </p>
        </div>
        <div class="card">
          <h3>Permissible use</h3>
          <p>
            Death data is powerful, so it must be used well. We provide it for legitimate
            verification such as fraud prevention, claims validation and program integrity. Our terms
            restrict use to those purposes, and we expect customers to apply their own review before
            any irreversible action on a consumer account.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- The security package. -->
  <section class="section section-alt">
    <div class="container">
      <div class="grid-2 align-start">
        <div>
          <span class="eyebrow">Vendor review</span>
          <h2 class="section-title">Everything in our security package</h2>
          <p class="section-lead">
            Most enterprise security reviews stall because the vendor answers slowly. Ask once and
            you get the whole set, plus a call with the person who owns our program.
          </p>
          <div class="actions-row">
            <a class="btn btn-primary" [routerLink]="[routes.contact.url]">Request the security package</a>
            <a class="btn btn-outline" [routerLink]="[routes.bookACall.url]">Book a review call</a>
          </div>
        </div>
        <ul class="check-list">
          @for (item of packageItems; track item) {
            <li>{{ item }}</li>
          }
        </ul>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Questions</span>
        <h2 class="section-title">Security and compliance questions</h2>
        <p class="section-lead">The eight questions that come up in every enterprise evaluation.</p>
      </div>
      <div class="faq-list">
        @for (faq of faqs; track faq.question) {
          <details class="faq-item">
            <summary>{{ faq.question }}</summary>
            <p>{{ faq.answer }}</p>
          </details>
        }
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="cta-band">
        <h2>Running a security or vendor review?</h2>
        <p>
          Our team will complete your questionnaire, walk through the control mapping and answer
          anything this page did not cover.
        </p>
        <div class="actions-row center">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.contact.url]">Contact our team</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.privacy.url]">Read the privacy policy</a>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />
